/*
 * Copyright (c) 2023 Trimble Inc. All rights reserved.
 */

import { categories, Category, CategoryType } from '../common/Categories';
import { loadFromLocalStorage } from '../common/localStorage';
import { fetchServiceCasesEndPoint, fetchLicensesEndPoint, fetchproductStatisticsEndPoint, fetchUserCaseStatisticsEndPointEndPoint } from '../common/constants';
import { asApplicationJsonBody, GridGraphqlExtras, isClosedServiceCaseCategory, toGridGraphqlBody, toServiceCaseDateGraphqlBody, toServiceCaseGridGraphqlBody } from '../common/graphqlJsonBody';

const requestChartDataType = 'REQUEST_CHART_DATA';
const requestFilterChartType = 'REQUEST_FILTER_CHART_DATA';
const recieveChartDataType = 'RECEIVE_CHART_DATA';
const setDialogOpenType = 'SET_DIALOG_OPEN';
const errorType = 'ERROR';

// Track abort controller to cancel in-flight chart requests
let chartAbortController: AbortController | undefined;

const initialState = {
    data: {},
    isLoading: false,
    dialogOpen: false,
    chartCompanyBreakdown: undefined as undefined | Array<{ companyName: string; count: number; licenseTypeSummary: string }>
}

export const actionCreators = {
    requestChartData: (id: number, startDate: Date, endDate: Date, extras?: GridGraphqlExtras) => async (dispatch: any, getState: any) => {
        const state = getState().dashboard || getState();
        const hasFilters = !!(extras && extras.filters && extras.filters.length);
        if (id === state.id && state.isLoading && !hasFilters) {
            return;
        }
        if (startDate === state.startDate && endDate === state.endDate && id === state.selectedCategoryId) {
            return;
        }
        
        // Cancel any in-flight chart request
        if (chartAbortController) {
            chartAbortController.abort();
        }
        chartAbortController = new AbortController();
        
        let selectedCategory: Category | undefined = categories.find((category: Category) => {
            return category.id === id;
        });
        selectedCategory = selectedCategory == undefined ? categories[0] : selectedCategory;
        if (!hasFilters) {
            dispatch({ type: requestChartDataType, id, startDate, endDate, selectedCategory });
        }
        else {
            dispatch({ type: requestFilterChartType, selectedCategory });
        }
        const token: string = loadFromLocalStorage('AuthToken');
        const encodedToken = 'Bearer ' + token;
        const headerDict: any = {
            'Authorization': encodedToken,
            'Content-Type': 'application/json'
        };
        const url = selectedCategory.type == CategoryType.ServiceCase ? fetchServiceCasesEndPoint : selectedCategory.type == CategoryType.License ? fetchLicensesEndPoint : '';
        if (selectedCategory.id == 5 && selectedCategory.requestBodyforProductChart != undefined && selectedCategory.requestBodyforUserChart != undefined) {
            try {
                if (navigator.onLine) {
                    const dateBody = (query: string) => toServiceCaseGridGraphqlBody(query, selectedCategory.id, startDate, endDate, extras);
                    let response = await fetch(fetchproductStatisticsEndPoint, {
                        headers: new Headers(headerDict),
                        body: dateBody(selectedCategory.requestBodyforProductChart),
                        method: 'POST',
                        signal: chartAbortController?.signal
                    });
                    const productstatisticsData = await response.json();
                    response = await fetch(fetchUserCaseStatisticsEndPointEndPoint, {
                        headers: new Headers(headerDict),
                        body: dateBody(selectedCategory.requestBodyforUserChart),
                        method: 'POST',
                        signal: chartAbortController?.signal
                    });
                    const userCaseStatisticsData = await response.json();
                    const responseData = {
                        data: {
                            productStatistics: productstatisticsData,
                            userCaseStatistics: userCaseStatisticsData
                        }
                    };
                    dispatch({ type: recieveChartDataType, selectedCategory, responseData });
                }
            } catch (e) {
                if ((e as any)?.name === 'AbortError') {
                    return;
                }
                if (navigator.onLine) {
                    dispatch({ type: errorType, e });
                }
            }
        }
        else {
            try {
                if (navigator.onLine) {
                    const chartQuery =
                        selectedCategory.type === CategoryType.License && hasFilters
                            ? (selectedCategory.requestBodyforChartFiltered ?? selectedCategory.requestBodyforChart)
                            : selectedCategory.requestBodyforChart;
                    const response = await fetch(url, {
                        headers: new Headers(headerDict),
                        body: selectedCategory.type === CategoryType.License
                            ? toGridGraphqlBody(chartQuery, extras)
                            : isClosedServiceCaseCategory(selectedCategory.id)
                                ? toServiceCaseDateGraphqlBody(chartQuery, selectedCategory.id, startDate, endDate)
                                : asApplicationJsonBody(chartQuery),
                        method: 'POST',
                        signal: chartAbortController?.signal
                    });
                    const responseData = await response.json();
                    dispatch({ type: recieveChartDataType, selectedCategory, responseData });
                }
            } catch (e) {
                if ((e as any)?.name === 'AbortError') {
                    return;
                }
                if (navigator.onLine) {
                    dispatch({ type: errorType, e });
                }
            }
        }

    },
    setDialogOpen: (open: boolean) => async (dispatch: any, getState: any) => {
        dispatch({ type: setDialogOpenType, payload: open });
    }
}

export const reducer = (state: any, action: any) => {
    state = state || initialState;

    switch (action.type) {
        case setDialogOpenType:
            return {
                ...state,
                dialogOpen: action.payload
            };
        case requestChartDataType:
            return {
                ...state,
                startDate: action.startDate,
                endDate: action.endDate,
                id: action.id,
                selectedCategory: action.selectedCategory,
                isLoading: true
            };
        case requestFilterChartType:
            return {
                ...state,
                isChartLoading: true,
            };
        case recieveChartDataType:
            switch (action.selectedCategory.id) {
                case 0:
                    return {
                        ...state,
                        data: action.responseData.data.myopenservicecases.data,
                        totalCount: action.responseData.data.myopenservicecases.count,
                        selectedCategory: action.selectedCategory,
                        isLoading: false,
                        isChartLoading: false
                    };
                case 1:
                    return {
                        ...state,
                        data: action.responseData.data.myclosedservicecases.data,
                        totalCount: action.responseData.data.myclosedservicecases.count,
                        selectedCategory: action.selectedCategory,
                        isLoading: false,
                        isChartLoading: false
                    };
                case 2:
                    return {
                        ...state,
                        data: action.responseData.data.mycompanyopenservicecases.data,
                        totalCount: action.responseData.data.mycompanyopenservicecases.count,
                        selectedCategory: action.selectedCategory,
                        isLoading: false,
                        isChartLoading: false
                    };
                case 3:
                    return {
                        ...state,
                        data: action.responseData.data.mycompanyclosedservicecases.data,
                        totalCount: action.responseData.data.mycompanyclosedservicecases.count,
                        selectedCategory: action.selectedCategory,
                        isLoading: false,
                        isChartLoading: false

                    };
                case 4:
                    if (action.responseData.data.mylicensechartsummary) {
                        const summary = action.responseData.data.mylicensechartsummary;
                        return {
                            ...state,
                            data: summary.barChartData,
                            chartCompanyBreakdown: undefined,
                            totalCount: summary.count,
                            selectedCategory: action.selectedCategory,
                            isLoading: false,
                            isChartLoading: false
                        };
                    }
                    return {
                        ...state,
                        data: action.responseData.data.mylicenses.data,
                        chartCompanyBreakdown: undefined,
                        totalCount: action.responseData.data.mylicenses.count,
                        selectedCategory: action.selectedCategory,
                        isLoading: false,
                        isChartLoading: false
                    };
                case 5:
                    return {
                        ...state,
                        data: action.responseData.data,
                        totalCount: action.responseData.data.productStatistics.data.allservicecases.count,
                        selectedCategory: action.selectedCategory,
                        isLoading: false,
                        isChartLoading: false
                    };

                default: return null;
            }

        case errorType:
            return {
                ...state,
                isLoading: false,
                error: action.e
            };
    }
    return state;
};
